import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import type { ColumnDef, OnChangeFn, SortingState } from "@tanstack/react-table";
import {
  DataTable,
  DataTablePagination,
} from "@/components/ui/data-table";
import { Affiliate } from "../../types/affiliate";
import { brandService } from "../../services/brand.service";
import { toast } from "react-hot-toast";
import { formatCurrency } from "../../utils/currency";
import { UserCheck, Clock, Trash2 } from "lucide-react";
import { formatstorageUrl } from "@/utils/formatters";

export type AffiliateSortBy = "NAME" | "EMAIL" | "REVENUE";
export type AffiliateSortOrder = "ASC" | "DESC";

interface ActiveAffiliatesTableProps {
  data: Affiliate[];
  page: number;
  limit: number;
  total: number;
  isLoading?: boolean;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  pageSizeOptions?: number[];
  disableRemoval?: boolean;
  /** Controlled sorting — pass both to enable sortable headers (server-side). */
  sorting?: SortingState;
  onSortingChange?: OnChangeFn<SortingState>;
}

export function ActiveAffiliatesTable({
  data,
  page,
  limit,
  total,
  isLoading,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions,
  disableRemoval = false,
  sorting,
  onSortingChange,
}: ActiveAffiliatesTableProps) {
  const navigate = useNavigate();
  const [selectedAffiliates, setSelectedAffiliates] = useState<string[]>([]);
  const queryClient = useQueryClient();

  const removeAffiliateMutation = useMutation({
    mutationFn: brandService.removeAffiliate,
    onSuccess: () => {
      toast.success("Affiliate removed successfully");
      queryClient.invalidateQueries({ queryKey: ["active-affiliates"] });
      setSelectedAffiliates([]);
    },
    onError: (error: unknown) => {
      // The backend refuses (409 PENDING_PAYOUT) while a payout is still
      // owed — surface its message rather than a generic failure.
      const message = (error as AxiosError<{ message?: string }>)?.response
        ?.data?.message;
      toast.error(message || "Failed to remove affiliate");
      console.error("Error removing affiliate:", error);
    },
  });

  const handleRemoveAffiliate = async (e: React.MouseEvent, id: string) => {
    setSelectedAffiliates([...selectedAffiliates, id]);
    e.stopPropagation();
    if (window.confirm("Are you sure you want to remove this affiliate?")) {
      removeAffiliateMutation.mutate(id);
    }
  };

  const handleBulkRemoveAffiliates = async () => {
    if (
      window.confirm(
        `Are you sure you want to remove ${selectedAffiliates.length} affiliates?`
      )
    ) {
      try {
        await Promise.all(
          selectedAffiliates.map((id) =>
            removeAffiliateMutation.mutateAsync(id)
          )
        );
        setSelectedAffiliates([]);
        toast.success("Selected affiliates removed successfully");
      } catch (error) {
        toast.error("Failed to remove some affiliates");
        console.error("Error removing affiliates:", error);
      }
    }
  };

  const handleRowClick = (id: string) => {
    navigate(`/brand/affiliates/${id}`);
  };

  const handleSelectAll = () => {
    if (selectedAffiliates.length === data.length) {
      setSelectedAffiliates([]);
    } else {
      setSelectedAffiliates(data.map((affiliate) => affiliate.id));
    }
  };

  const handleSelectAffiliate = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    setSelectedAffiliates((prev) =>
      prev.includes(id) ? prev.filter((affId) => affId !== id) : [...prev, id]
    );
  };

  const totalPages = Math.max(Math.ceil(total / limit), 1);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const columns: ColumnDef<Affiliate, any>[] = [
    ...(!disableRemoval
      ? [
          {
            id: "select",
            header: () => null,
            cell: ({ row }: { row: { original: Affiliate } }) => (
              <input
                type="checkbox"
                checked={selectedAffiliates.includes(row.original.id)}
                onChange={() => {}}
                onClick={(e) => handleSelectAffiliate(e, row.original.id)}
                className="rounded border-border text-brand-600 focus:ring-brand-500"
              />
            ),
            meta: {
              width: "w-10",
              skeleton: (
                <div className="h-4 w-4 rounded bg-muted animate-pulse" />
              ),
            },
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
          } as ColumnDef<Affiliate, any>,
        ]
      : []),
    {
      id: "NAME",
      header: "Name",
      enableSorting: true,
      cell: ({ row }) => (
        <div className="flex items-center gap-3 min-w-0">
          <img
            src={formatstorageUrl(row.original.profileImage) || `/profile.jpg`}
            alt={`${row.original.firstName} ${row.original.lastName}`}
            className="w-8 h-8 rounded-full shrink-0"
          />
          <span className="break-words font-medium text-foreground">
            {row.original.firstName} {row.original.lastName}
          </span>
        </div>
      ),
      meta: {
        width: "w-[20%]",
        skeleton: (
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 rounded-full bg-muted animate-pulse" />
            <div className="h-3.5 w-28 rounded bg-muted animate-pulse" />
          </div>
        ),
      },
    },
    {
      id: "EMAIL",
      header: "Email address",
      enableSorting: true,
      cell: ({ row }) => (
        <span className="block break-all text-muted-foreground">
          {row.original.email}
        </span>
      ),
      meta: { width: "w-[22%]" },
    },
    {
      id: "code",
      header: "Code",
      cell: ({ row }) => (
        <span className="text-muted-foreground">{row.original.code}</span>
      ),
      meta: { width: "w-28" },
    },
    {
      id: "REVENUE",
      header: "Revenue",
      enableSorting: true,
      cell: ({ row }) => (
        <span className="text-muted-foreground">
          {formatCurrency(row.original.revenue || 0)}
        </span>
      ),
      meta: { width: "w-28" },
    },
    {
      id: "profileStatus",
      header: "Profile Status",
      cell: ({ row }) => {
        const isCompleted = row.original.isProfileCompleted ?? true;

        return (
          <div className="flex items-center gap-2">
            {isCompleted ? (
              <>
                <UserCheck className="w-4 h-4 text-green-600" />
                <span className="text-sm font-medium text-green-600">
                  Completed
                </span>
              </>
            ) : (
              <>
                <Clock className="w-4 h-4 text-orange-600" />
                <span className="text-sm font-medium text-orange-600">
                  Incomplete
                </span>
              </>
            )}
          </div>
        );
      },
      meta: { width: "w-36" },
    },
    ...(!disableRemoval
      ? [
          {
            id: "actions",
            header: "Actions",
            cell: ({ row }: { row: { original: Affiliate } }) => {
              const isRemoving =
                removeAffiliateMutation.isPending &&
                selectedAffiliates.includes(row.original.id);
              return (
                <button
                  onClick={(e) => handleRemoveAffiliate(e, row.original.id)}
                  className="rounded-md p-1.5 text-muted-foreground hover:bg-rose-50 dark:hover:bg-rose-950/40 hover:text-rose-600 dark:hover:text-rose-400 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={removeAffiliateMutation.isPending}
                  aria-label={isRemoving ? "Removing…" : "Remove affiliate"}
                  title={isRemoving ? "Removing…" : "Remove affiliate"}
                >
                  {isRemoving ? (
                    <span className="block h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" />
                  ) : (
                    <Trash2 className="w-3.5 h-3.5" />
                  )}
                </button>
              );
            },
            meta: {
              width: "w-28",
              skeleton: (
                <div className="h-6 w-16 rounded bg-muted animate-pulse" />
              ),
            },
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
          } as ColumnDef<Affiliate, any>,
        ]
      : []),
  ];

  return (
    <div>
      {!disableRemoval && data.length > 0 && (
        <div className="p-4 border-b border-border flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={
                data.length > 0 && selectedAffiliates.length === data.length
              }
              onChange={handleSelectAll}
              className="rounded border-border text-brand-600 focus:ring-brand-500"
            />
            <span className="text-sm text-muted-foreground">
              {selectedAffiliates.length === 0
                ? "Select all"
                : selectedAffiliates.length === data.length
                ? "Unselect all"
                : `Selected ${selectedAffiliates.length}`}
            </span>
          </div>
          {selectedAffiliates.length > 0 && (
            <div className="flex items-center space-x-4">
              <button
                className="text-sm text-red-600 hover:text-red-700"
                onClick={handleBulkRemoveAffiliates}
                disabled={removeAffiliateMutation.isPending}
              >
                {removeAffiliateMutation.isPending
                  ? "Removing..."
                  : "Remove selected"}
              </button>
            </div>
          )}
        </div>
      )}
      <DataTable
        columns={columns}
        data={data}
        isLoading={isLoading}
        emptyText="No affiliates found"
        getRowId={(row) => row.id}
        onRowClick={(row) => handleRowClick(row.id)}
        sorting={sorting}
        onSortingChange={onSortingChange}
        manualSorting
      />
      <DataTablePagination
        page={page}
        totalPages={totalPages}
        total={total}
        pageSize={limit}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        pageSizeOptions={pageSizeOptions}
        noun="affiliates"
      />
    </div>
  );
}
